import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Calendar,
  Settings,
  UserCircle,
  Dumbbell,
  Apple,
  HeartPulse,
  Crown,
  TrendingUp,
  BookOpen,
  MessageSquare
} from "lucide-react";
import { useState } from "react";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { Separator } from "@/components/ui/separator";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { PremiumBadge } from "@/components/ui/premium-badge";
import { PremiumFeatureDialog } from "@/components/ui/premium-feature-dialog";
import type { SubscriptionFeatures } from "@/types/subscription";
import { useTranslations } from "@/hooks/useTranslations";
import { ScrollArea } from "@/components/ui/scroll-area";

export const DashboardSidebar = () => {
  const location = useLocation();
  const { isMenuOpen, closeMenu } = useMobileMenu();
  const { t } = useTranslations('dashboard');
  const { getPath } = useLanguagePath();
  const { isPremiumFeature, hasFeature } = useSubscriptionFeatures();
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);

  const mainItems = [
    {
      title: t('sidebar.overview'),
      icon: LayoutDashboard,
      href: getPath("/dashboard"),
    },
    {
      title: t('sidebar.schedule'),
      icon: Calendar,
      href: getPath("/dashboard/schedule"),
    },
    {
      title: t('sidebar.students'),
      icon: Users,
      href: getPath("/dashboard/students"),
    },
    {
      title: t('sidebar.workouts'),
      icon: Dumbbell,
      href: getPath("/dashboard/workouts"),
    },
    {
      title: t('sidebar.sessions'),
      icon: ClipboardList,
      href: getPath("/dashboard/training-sessions"),
    },
    {
      title: t('sidebar.nutrition'),
      icon: Apple,
      href: getPath("/dashboard/nutrition"),
      featureKey: 'nutrition' as keyof SubscriptionFeatures,
    },
    {
      title: t('sidebar.assessments'),
      icon: HeartPulse,
      href: getPath("/dashboard/assessments"),
    },
    {
      title: t('sidebar.progress'),
      icon: TrendingUp,
      href: getPath("/dashboard/progress"),
    },
    {
      title: t('sidebar.exerciseLibrary'),
      icon: BookOpen,
      href: getPath("/dashboard/exercise-library"),
    },
    {
      title: t('sidebar.profile'),
      icon: UserCircle,
      href: getPath("/dashboard/profile"),
    },
    {
      title: t('sidebar.messages'),
      icon: MessageSquare,
      href: getPath("/dashboard/messages"),
    },
  ];

  const footerItems = [
    {
      title: t('sidebar.plan'),
      icon: Crown,
      href: getPath("/dashboard/plan"),
    },
    {
      title: t('sidebar.settings'),
      icon: Settings,
      href: getPath("/dashboard/settings"),
    },
  ];

  const handleLinkClick = () => {
    closeMenu();
  };

  const isPathActive = (href: string) => {
    const currentPath = location.pathname.replace(/^\/pt/, '');
    const pathWithoutPrefix = href.replace(/^\/pt/, '');
    
    return currentPath === pathWithoutPrefix;
  };

  const renderMenuItem = (item: typeof mainItems[0] | typeof footerItems[0]) => {
    const isNutritionItem = 'featureKey' in item && item.featureKey === 'nutrition';
    const canAccessFeature = !isNutritionItem || hasFeature('nutrition');

    const handleItemClick = (e: React.MouseEvent) => {
      if (isNutritionItem && !canAccessFeature) {
        e.preventDefault();
        setShowPremiumDialog(true);
        return;
      }
      handleLinkClick();
    };

    return (
      <div key={item.href} className="relative">
        <Link
          to={item.href}
          onClick={handleItemClick}
          className={cn(
            "flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200",
            isPathActive(item.href) 
              ? "bg-flame/10 text-flame shadow-flame/20" 
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
          )}
        >
          <item.icon className={cn(
            "h-5 w-5",
            isPathActive(item.href)
              ? "text-flame"
              : "text-gray-500 dark:text-gray-400"
          )} />
          {item.title}
          {'featureKey' in item && isPremiumFeature(item.featureKey) && (
            <div className="absolute top-2 right-10">
              <PremiumBadge />
            </div>
          )}
        </Link>
      </div>
    );
  };

  return (
    <>
      <div 
        className={cn(
          "fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300 md:hidden dark:bg-black/50",
          isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />
      
      <nav 
        className={cn(
          "fixed top-20 left-0 bottom-0 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg z-60 transform transition-transform duration-300 ease-out md:translate-x-0 md:hidden border border-gray-100 dark:border-gray-700 rounded-xl shadow-md font-mono",
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col justify-between h-full max-h-full p-4">
          <ScrollArea className="flex-1 pr-4">
            <div className="flex flex-col gap-1">
              {mainItems.map(renderMenuItem)}
            </div>
          </ScrollArea>
          <div>
            <Separator className="mb-1 mt-3" />
            <div className="flex flex-col gap-1">
              {footerItems.map(renderMenuItem)}
            </div>
          </div>
        </div>
      </nav>
      
      <aside className="fixed top-20 left-4 bottom-2 w-64 hidden md:flex flex-col justify-between border border-gray-100 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-xl shadow-md hover:shadow-lg transition-all duration-300 font-mono">
        <div className="flex flex-col justify-between h-full">
          <ScrollArea className="flex-1 px-4 pt-4">
            <div className="flex flex-col gap-1">
              {mainItems.map(renderMenuItem)}
            </div>
          </ScrollArea>
          <div className="mb-2 px-4">
            <Separator className="mb-2" />
            <div className="flex flex-col gap-1">
              {footerItems.map(renderMenuItem)}
            </div>
          </div>
        </div>
      </aside>

      <PremiumFeatureDialog
        open={showPremiumDialog}
        onOpenChange={setShowPremiumDialog}
        featureName="nutrition"
        icon={<Apple className="h-5 w-5 text-amber-500" />}
      />
    </>
  );
};
